import React, { useState, useEffect } from 'react';
import { I18n } from '@aws-amplify/core';
import { Row, Col, Form, message, Input, Skeleton } from 'antd';

import { Title } from '@/components/Typography';
import { useUpdateBeraReport } from '@/hooks/useUpdateBeraReport';
import { useApplicationContext } from '@/context/v1/Application/context';

const { TextArea } = Input;

interface CommentProps {
	isLoading?: boolean;
	consolidated?: boolean;
	bera_report_id: string;
	onEditComment(editingComment: boolean): void;
}

export function Comment({
	isLoading = false,
	consolidated = false,
	bera_report_id,
	onEditComment,
}: Readonly<CommentProps>): JSX.Element {
	const [text, setText] = useState<string | null>(null);
	const [debounceTimer, setDebounceTimer] = useState<NodeJS.Timeout | null>(null);

	const { organization, company } = useApplicationContext();
	const [messageApi, contextHolder] = message.useMessage();
	const { mutateAsync: updateBeraReport } = useUpdateBeraReport();

	function handleOnChange(e: any): void {
		setText(e.target.value);

		if (debounceTimer) {
			clearTimeout(debounceTimer);
		}

		const timer = setTimeout(() => {
			if (e.target.value.length > 0) {
				const parameters = {
					organization_id: organization?.id,
					company_id: company?.id,
					id: bera_report_id,
					comment: e.target.value
				};

				updateBeraReport(parameters, {
					onSuccess,
					onError,
					onSettled
				});
			}
		}, 1000);

		setDebounceTimer(timer);
	}

	function handleOnBlur(): void {
		if (debounceTimer) {
			clearTimeout(debounceTimer);
		}

		if (text && text.length > 0) {
			const parameters = {
				organization_id: organization?.id,
				company_id: company?.id,
				id: bera_report_id,
				comment: text
			};

			updateBeraReport(parameters, {
				onSuccess,
				onError,
				onSettled
			});
		}
	}

	const onSuccess = () => {
		messageApi.open({
			type: 'success',
			content: I18n.get('Updated comment successfully'),
			duration: 2.5
		});
	};

	const onError = () => {
		message.error(I18n.get('Fail to save the comments'));
	};

	const onSettled = () => onEditComment(false);

	useEffect(() => {
		return () => {
			if (debounceTimer) {
				clearTimeout(debounceTimer);
			}
		};
	}, [debounceTimer]);

	return (
		<Row gutter={[0, 24]}>
			<Col span={24}>
				<Row gutter={[0, 16]}>
					<Col span={24}>
						<Title style={{ margin: 0 }} level={5}>
							Comment
						</Title>
						{contextHolder}
					</Col>
					<Col span={24}>
						{isLoading ? (
							<Skeleton.Input block active />
						) : (
							<Form.Item name="comment" labelCol={{ span: 24 }}>
								<TextArea
									rows={4}
									maxLength={500}
									showCount={true}
									onBlur={handleOnBlur}
									onChange={handleOnChange}
									disabled={consolidated}
								/>
							</Form.Item>
						)}
					</Col>
				</Row>
			</Col>
		</Row>
	);
}
